// Theme Configuration System for MSA System
export interface ThemeColors {
  // Primary Brand Colors
  primary: string;
  primaryLight: string;
  primaryDark: string;
  
  // Secondary Colors
  secondary: string;
  secondaryLight: string;
  secondaryDark: string;
  
  // Accent Colors
  accent1: string;
  accent2: string;
  accent3: string;
  
  // Navigation Colors
  navigationBackground: string;
  navigationText: string;
  navigationTextSecondary: string;
  navigationHover: string;
  navigationActive: string;
  navigationBorder: string;
  
  // Background Colors
  heroBackground: string;
  mainBackground: string;
  cardBackground: string;
  sectionBackground: string;
  
  // Text Colors
  textPrimary: string;
  textSecondary: string;
  textLight: string;
  textHero: string;
  
  // Border Colors
  borderLight: string;
  borderMedium: string;
  borderDark: string;
  
  // Status Colors
  success: string;
  warning: string;
  info: string;
  error: string;
  
  // Gradient Colors
  gradientStart: string;
  gradientMiddle: string;
  gradientEnd: string;
}

export interface Theme {
  name: string;
  displayName: string;
  description: string;
  colors: ThemeColors;
}

// Default Theme (Current Blue/Teal Theme)
export const defaultTheme: Theme = {
  name: 'default',
  displayName: 'Black',
  description: 'Professional blue and teal color scheme',
  colors: {
    primary: '#232f3e',
    primaryLight: '#78dce8',
    primaryDark: '#0f1419',
    
    secondary: '#ff9966',
    secondaryLight: '#ffa366',
    secondaryDark: '#e6804d',
    
    accent1: '#78dce8',
    accent2: '#ff9966',
    accent3: '#a9dc76',
    
    // Navigation Colors
    navigationBackground: '#232f3e',
    navigationText: '#ffffff',
    navigationTextSecondary: 'rgba(255, 255, 255, 0.7)',
    navigationHover: 'rgba(120, 220, 232, 0.1)',
    navigationActive: 'rgba(120, 220, 232, 0.2)',
    navigationBorder: '#0f1419',
    
    heroBackground: 'linear-gradient(135deg, #0f1419 0%, #232f3e 50%, #16202b 100%)',
    mainBackground: 'linear-gradient(180deg, #ffffff 0%, #f8f9fa 100%)',
    cardBackground: '#ffffff',
    sectionBackground: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
    
    textPrimary: '#232f3e',
    textSecondary: '#5f6b7a',
    textLight: '#d5dbdb',
    textHero: '#ffffff',
    
    borderLight: '#e1e4e8',
    borderMedium: '#d1d5db',
    borderDark: '#9ca3af',
    
    success: '#28a745',
    warning: '#ffc107',
    info: '#007bff',
    error: '#dc3545',
    
    gradientStart: '#ff9966',
    gradientMiddle: '#78dce8',
    gradientEnd: '#a9dc76'
  }
};

// Amazon Orange Theme (Professional Orange with Platform Overview Aligned Sidebar)
export const amazonOrangeTheme: Theme = {
  name: 'amazon-orange',
  displayName: 'Amazon Orange',
  description: 'Professional Amazon orange with platform overview aligned sidebar',
  colors: {
    primary: '#ff9900',
    primaryLight: '#ffad33',
    primaryDark: '#e68a00',
    
    secondary: '#232f3e',
    secondaryLight: '#3a4a5c',
    secondaryDark: '#1a252f',
    
    accent1: '#ff9900',
    accent2: '#232f3e',
    accent3: '#f90',
    
    // Navigation Colors - Aligned with Platform Overview
    navigationBackground: 'linear-gradient(135deg, #ffffff 0%, #fff8f0 100%)', // Match sectionBackground
    navigationText: '#232f3e', // Match textPrimary
    navigationTextSecondary: '#ff9900', // Match textSecondary
    navigationHover: 'rgba(255, 228, 184, 0.3)', // Based on borderLight
    navigationActive: 'rgba(255, 153, 0, 0.1)', // Based on primary color
    navigationBorder: '#ffe4b8', // Match borderLight
    
    heroBackground: 'linear-gradient(135deg, #232f3e 0%, #ff9900 90%, #ffad33 100%)',
    mainBackground: 'linear-gradient(180deg, #ffffff 0%, #fff8f0 100%)',
    cardBackground: '#ffffff',
    sectionBackground: 'linear-gradient(135deg, #ffffff 0%, #fff8f0 100%)',
    
    textPrimary: '#232f3e',
    textSecondary: '#ff9900',
    textLight: '#ffffff',  // 改为白色以提高在橙色背景下的对比度
    textHero: '#ffffff',   // 保持白色确保在深色背景下清晰可见
    
    borderLight: '#ffe4b8',
    borderMedium: '#ffd93d',
    borderDark: '#ff9900',
    
    success: '#28a745',
    warning: '#ff9900',
    info: '#007bff',
    error: '#dc3545',
    
    gradientStart: '#232f3e',
    gradientMiddle: '#ff9900',
    gradientEnd: '#ffad33'
  }
};

// Sea Blue Theme (Ocean Tones with Platform Overview Aligned Sidebar)
export const seaBlueTheme: Theme = {
  name: 'sea-blue',
  displayName: 'Sea Blue',
  description: 'Calming sea blue background with platform overview aligned sidebar',
  colors: {
    primary: '#006064',
    primaryLight: '#e0f7fa',
    primaryDark: '#004d52',
    
    secondary: '#00bcd4',
    secondaryLight: '#4dd0e1',
    secondaryDark: '#0097a7',
    
    accent1: '#006064',
    accent2: '#00bcd4',
    accent3: '#26c6da',
    
    // Navigation Colors - Aligned with Platform Overview
    navigationBackground: 'rgba(255, 255, 255, 0.7)', // Match sectionBackground
    navigationText: '#004d52', // Match textPrimary
    navigationTextSecondary: '#00695c', // Match textSecondary
    navigationHover: 'rgba(178, 223, 219, 0.3)', // Based on borderLight
    navigationActive: 'rgba(0, 188, 212, 0.1)', // Based on accent color
    navigationBorder: '#b2dfdb', // Match borderLight
    
    heroBackground: 'linear-gradient(135deg, #006064 0%, #00838f 50%, #0097a7 100%)',
    mainBackground: 'linear-gradient(180deg, #e0f2f1 0%, #b2dfdb 100%)',
    cardBackground: 'rgba(255, 255, 255, 0.9)',
    sectionBackground: 'rgba(255, 255, 255, 0.7)',
    
    textPrimary: '#004d52',
    textSecondary: '#00695c',
    textLight: '#ffffff',
    textHero: '#ffffff',
    
    borderLight: '#b2dfdb',
    borderMedium: '#80cbc4',
    borderDark: '#4db6ac',
    
    success: '#00c853',
    warning: '#ff9800',
    info: '#00bcd4',
    error: '#f44336',
    
    gradientStart: '#00bcd4',
    gradientMiddle: '#4dd0e1',
    gradientEnd: '#26c6da'
  }
};

// Professional Blue Theme (Clean Blue with Platform Overview Aligned Sidebar)
export const professionalBlueTheme: Theme = {
  name: 'professional-blue',
  displayName: 'Professional Blue',
  description: 'Clean professional blue with platform overview aligned sidebar',
  colors: {
    primary: '#1e3a8a',
    primaryLight: '#3b82f6',
    primaryDark: '#1e40af',
    
    secondary: '#0ea5e9',
    secondaryLight: '#38bdf8',
    secondaryDark: '#0284c7',
    
    accent1: '#3b82f6',
    accent2: '#0ea5e9',
    accent3: '#06b6d4',
    
    // Navigation Colors - Aligned with Platform Overview
    navigationBackground: 'linear-gradient(135deg, #ffffff 0%, #f0f9ff 100%)', // Match sectionBackground
    navigationText: '#1e3a8a', // Match textPrimary
    navigationTextSecondary: '#475569', // Match textSecondary
    navigationHover: 'rgba(224, 242, 254, 0.3)', // Based on borderLight
    navigationActive: 'rgba(59, 130, 246, 0.1)', // Based on accent color
    navigationBorder: '#e0f2fe', // Match borderLight
    
    heroBackground: 'linear-gradient(135deg, #1e3a8a 0%, #1e40af 50%, #1d4ed8 100%)',
    mainBackground: 'linear-gradient(180deg, #ffffff 0%, #f0f9ff 100%)',
    cardBackground: '#ffffff',
    sectionBackground: 'linear-gradient(135deg, #ffffff 0%, #f0f9ff 100%)',
    
    textPrimary: '#1e3a8a',
    textSecondary: '#475569',
    textLight: '#bfdbfe',
    textHero: '#ffffff',
    
    borderLight: '#e0f2fe',
    borderMedium: '#bae6fd',
    borderDark: '#7dd3fc',
    
    success: '#059669',
    warning: '#d97706',
    info: '#0284c7',
    error: '#dc2626',
    
    gradientStart: '#3b82f6',
    gradientMiddle: '#0ea5e9',
    gradientEnd: '#06b6d4'
  }
};

// All available themes
export const themes: Theme[] = [
  defaultTheme,
  //purpleTheme,
  //greenTheme,
  //redTheme,
 // darkTheme,
  amazonOrangeTheme,
  seaBlueTheme,
  professionalBlueTheme
];

// Theme utility functions
export const getThemeByName = (name: string): Theme => {
  return themes.find(theme => theme.name === name) || amazonOrangeTheme;
};

export const applyTheme = (theme: Theme): void => {
  const root = document.documentElement;
  
  // Add theme name as data attribute to body for CSS targeting
  document.body.setAttribute('data-theme', theme.name);
  
  // Apply all theme colors as CSS custom properties
  Object.entries(theme.colors).forEach(([key, value]) => {
    root.style.setProperty(`--theme-${key}`, value);
  });
  
  // Additional global theme variables for comprehensive theming
  root.style.setProperty('--theme-background', theme.colors.mainBackground);
  root.style.setProperty('--theme-contentBackground', theme.colors.cardBackground);
  root.style.setProperty('--theme-sidebarBackground', theme.colors.cardBackground);
  root.style.setProperty('--theme-navigationBackground', theme.colors.navigationBackground);
  root.style.setProperty('--theme-navigationText', theme.colors.navigationText);
  root.style.setProperty('--theme-navigationTextSecondary', theme.colors.navigationTextSecondary);
  root.style.setProperty('--theme-navigationHover', theme.colors.navigationHover);
  root.style.setProperty('--theme-navigationActive', theme.colors.navigationActive);
  root.style.setProperty('--theme-navigationBorder', theme.colors.navigationBorder);
  root.style.setProperty('--theme-borderColor', theme.colors.borderLight);
  root.style.setProperty('--theme-hoverBackground', theme.colors.borderLight);
  root.style.setProperty('--theme-activeBackground', theme.colors.borderMedium);
  root.style.setProperty('--theme-cardHeaderBackground', theme.colors.sectionBackground);
  root.style.setProperty('--theme-buttonText', theme.colors.textHero);
  root.style.setProperty('--theme-buttonSecondaryBackground', theme.colors.cardBackground);
  root.style.setProperty('--theme-linkColor', theme.colors.info);
  root.style.setProperty('--theme-linkHoverColor', theme.colors.primaryDark);
  root.style.setProperty('--theme-inputBackground', theme.colors.cardBackground);
  root.style.setProperty('--theme-tableHeaderBackground', theme.colors.sectionBackground);
  root.style.setProperty('--theme-infoBackground', theme.colors.borderLight);
  root.style.setProperty('--theme-infoColor', theme.colors.info);
  root.style.setProperty('--theme-successBackground', theme.colors.borderLight);
  root.style.setProperty('--theme-successColor', theme.colors.success);
  root.style.setProperty('--theme-warningBackground', theme.colors.borderLight);
  root.style.setProperty('--theme-warningColor', theme.colors.warning);
  root.style.setProperty('--theme-errorBackground', theme.colors.borderLight);
  root.style.setProperty('--theme-errorColor', theme.colors.error);
  root.style.setProperty('--theme-badgeBackground', theme.colors.borderMedium);
  root.style.setProperty('--theme-badgeText', theme.colors.textPrimary);
  root.style.setProperty('--theme-modalBackground', theme.colors.cardBackground);
  root.style.setProperty('--theme-modalHeaderBackground', theme.colors.sectionBackground);
  root.style.setProperty('--theme-focusRing', `${theme.colors.primary}33`);
  root.style.setProperty('--theme-scrollbarTrack', theme.colors.borderLight);
  root.style.setProperty('--theme-scrollbarThumb', theme.colors.borderMedium);
  root.style.setProperty('--theme-scrollbarThumbHover', theme.colors.borderDark);
  root.style.setProperty('--theme-selectionBackground', `${theme.colors.primary}33`);
  root.style.setProperty('--theme-selectionText', theme.colors.textPrimary);
  root.style.setProperty('--theme-fontFamily', "'Amazon Ember', 'Helvetica Neue', Roboto, Arial, sans-serif");
  
  // Store current theme in localStorage
  localStorage.setItem('msa-theme', theme.name);
};

export const getCurrentTheme = (): Theme => {
  const savedTheme = localStorage.getItem('msa-theme');
  return savedTheme ? getThemeByName(savedTheme) : amazonOrangeTheme;
};
